import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { MoreVertical } from 'lucide-react';
import { MouseEvent, useState } from 'react';

import { DeleteQueryModal } from '@/sections/insights/queryBuilder/components/Modals';
import { useUnsavedChangesPrompt } from '@/sections/insights/queryBuilder/components/QueryBuilderSidebar/useUnsavedChangesPrompt';
import { useDeleteQuery } from '@/sections/insights/queryBuilder/hooks/useDeleteQuery';
import { QueryFolders } from '@/sections/insights/queryBuilder/savedQueries/folders';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

interface QueryMenuProps {
  /** The query to display actions for */
  query: SavedQuery;
}

/**
 * QueryMenu - Three-dot menu for query items in the Query Builder sidebar.
 *
 * Provides context-specific actions based on query type:
 * - Non-custom queries: View Query → Opens in read-only view mode
 * - Custom queries: Edit Query → Opens in edit mode for modification
 *
 * Duplication functionality is available via the Duplicate button in QueryViewer
 * for both query types, eliminating the need for duplicate action in this menu.
 *
 * The menu appears on hover via the parent's group-hover pattern and uses
 * Headless UI Menu for accessibility and interaction handling.
 *
 * @example
 * ```tsx
 * <QueryMenu query={savedQuery} />
 * ```
 */
export const QueryMenu = ({ query }: QueryMenuProps) => {
  const isCustomQuery = query.folderId === QueryFolders.CUSTOM_QUERIES;
  const { queryState, setMode, setQuery } = useQueryBuilder();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const deleteMutation = useDeleteQuery();
  const { confirmIfDirty, modal } = useUnsavedChangesPrompt();

  /**
   * Handler for View Query action
   * Sets workstation to view mode and selects the query
   */
  const handleViewQuery = () => {
    if (queryState?.metadata.id === query.id) {
      setMode('view');
      return;
    }

    confirmIfDirty(() => {
      setQuery(query);
      setMode('view');
    });
  };

  /**
   * Handler for Edit Query action (custom queries only)
   * Sets workstation to edit mode for modifying the query
   */
  const handleEditQuery = () => {
    if (queryState?.metadata.id === query.id) {
      setMode('edit');
      return;
    }

    confirmIfDirty(() => {
      setQuery(query);
      setMode('edit');
    });
  };

  /**
   * Handler for Delete Query action (custom queries only)
   * Opens confirmation modal before deleting
   */
  const handleDeleteQuery = () => {
    setIsDeleteModalOpen(true);
  };

  /**
   * Confirm deletion handler
   * Executes DELETE mutation and closes modal on success
   */
  const handleConfirmDelete = () => {
    deleteMutation.mutate({ queryId: query.id });
  };

  return (
    <Menu as="div" className="relative">
      <MenuButton
        onClick={e => e.stopPropagation()}
        className="text-chariot-text-secondary hover:text-chariot-text-primary rounded p-1 transition-colors"
        data-testid={`query-menu-button-${query.id}`}
      >
        <MoreVertical className="size-4" />
      </MenuButton>

      <MenuItems
        anchor="bottom end"
        className="bg-chariot-background-primary border-chariot-stroke z-50 mt-2 w-48 rounded-md border shadow-lg"
      >
        <div className="py-1">
          {!isCustomQuery && (
            <QueryMenuItem
              label="View Query"
              onClick={() => {
                if (queryState?.metadata.id === query.id) {
                  setMode('split-view');
                  return;
                }

                confirmIfDirty(() => {
                  setQuery(query);
                  setMode('split-view');
                });
              }}
              testId={`query-menu-view-simple-${query.id}`}
            />
          )}

          {!isCustomQuery && (
            <QueryMenuItem
              label="View Query (Graph)"
              onClick={handleViewQuery}
              testId={`query-menu-view-graph-${query.id}`}
            />
          )}

          {isCustomQuery && (
            <QueryMenuItem
              label="Edit Query"
              onClick={() => {
                if (queryState?.metadata.id === query.id) {
                  setMode('split-edit');
                  return;
                }

                confirmIfDirty(() => {
                  setQuery(query);
                  setMode('split-edit');
                });
              }}
              testId={`query-menu-edit-simple-${query.id}`}
            />
          )}

          {isCustomQuery && (
            <QueryMenuItem
              label="Edit Query (Graph)"
              onClick={handleEditQuery}
              testId={`query-menu-edit-graph-${query.id}`}
            />
          )}

          {isCustomQuery && (
            <QueryMenuItem
              label="Edit Query (JSON)"
              onClick={() => {
                if (queryState?.metadata.id === query.id) {
                  setMode('split-writer');
                  return;
                }

                confirmIfDirty(() => {
                  setQuery(query);
                  setMode('split-writer');
                });
              }}
              testId={`query-menu-edit-json-${query.id}`}
            />
          )}

          {isCustomQuery && (
            <QueryMenuItem
              label="Delete Query"
              onClick={handleDeleteQuery}
              testId={`query-menu-delete-${query.id}`}
            />
          )}
        </div>
      </MenuItems>

      <DeleteQueryModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        query={query}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteMutation.isPending}
      />

      {modal}
    </Menu>
  );
};

interface QueryMenuItemProps {
  /** Display text for the menu item */
  label: string;
  /** Click handler */
  onClick: () => void;
  /** Test ID for the menu item */
  testId: string;
}

/**
 * QueryMenuItem - Individual menu item component
 */
const QueryMenuItem = ({ label, onClick, testId }: QueryMenuItemProps) => {
  const handleClick = (e: MouseEvent) => {
    e.stopPropagation(); // Prevent click from bubbling to parent query item
    onClick();
  };

  return (
    <MenuItem>
      {({ focus }) => (
        <button
          onClick={handleClick}
          className={`${
            focus ? 'bg-chariot-background-secondary' : ''
          } text-chariot-text-primary w-full px-4 py-2 text-left text-sm`}
          data-testid={testId}
        >
          {label}
        </button>
      )}
    </MenuItem>
  );
};
